import Alert from "@mui/material/Alert";
import Logger from "../../../../services/Logger";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import CustomButton from "../../../components/CustomButton";
import DictionaryApiService from "../../../system/integrate/DictionaryApiService";

export default function GirdCanAssocConfirmDialog({ 
    open, 
    setOpen, 
    word, 
    title, 
    onConfirm,
    language = 'english',
    emptyCellCount = null
}) {
    const wordLength = word ? word.length : 0;
    const countsMismatch = emptyCellCount != null && wordLength !== Number(emptyCellCount);
    const [loading, setLoading] = useState(false);
    const [wordMeaning, setWordMeaning] = useState(null);
    const [error, setError] = useState(null);
    const [editedTitle, setEditedTitle] = useState(title || '');

    useEffect(() => {
        if (open && word) {
            fetchWordMeaning();
        } else {
            // Reset state when dialog closes
            setWordMeaning(null);
            setError(null);
        }
    }, [open, word]);

    useEffect(() => {
        // Update editedTitle when title prop changes
        setEditedTitle(title || '');
    }, [title]);

    const fetchWordMeaning = async () => {
        if (!word) return;
        
        setLoading(true);
        setError(null);
        try {
            const result = await DictionaryApiService.getWord(word, language);
            setWordMeaning(result);
        } catch (err) {
            Logger.error(`Error fetching word meaning for "${word}":`, err);
            setError("Failed to fetch word meaning");
        } finally {
            setLoading(false);
        }
    };

    const handleYes = () => {
        setOpen(false);
        if (onConfirm) {
            onConfirm(editedTitle);
        }
    };

    const handleNo = () => {
        setOpen(false);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const formatMeaningParts = (meaning) => {
        if (!meaning || typeof meaning !== 'object') return [];
        return Object.entries(meaning)
            .filter(([key, value]) => key !== 'title' && value)
            .map(([key, value]) => ({ key, value: String(value) }));
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose} 
            aria-labelledby="alert-dialog-title" 
            aria-describedby="alert-dialog-description" 
            maxWidth="sm" 
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">{title || "Place Word in Puzzle"}</DialogTitle>
            <DialogContent>
                {word != null && word !== '' && (
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', wordBreak: 'break-all' }}>
                            {(word || '').toUpperCase()}
                        </Typography>
                    </Box>
                )}

                {/* Word Meaning */}
                <Box sx={{ mb: 2 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : error ? (
                        <Alert severity="error">{error}</Alert>
                    ) : wordMeaning ? (
                        <Box sx={{ 
                            p: 1.5, 
                            backgroundColor: 'action.hover', 
                            borderRadius: 1,
                            maxHeight: 200,
                            overflow: 'auto'
                        }}>
                            {wordMeaning.info?.score != null && (
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <Typography component="span" sx={{ fontWeight: 'bold' }}>score: </Typography>
                                    {wordMeaning.info.score.toLocaleString()}
                                </Typography>
                            )}
                            {formatMeaningParts(wordMeaning.meaning).length > 0 ? (
                                formatMeaningParts(wordMeaning.meaning).map(({ key, value }) => (
                                    <Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
                                        <Typography component="span" sx={{ fontWeight: 'bold' }}>{key}: </Typography>
                                        {value}
                                    </Typography>
                                ))
                            ) : (
                                <Typography variant="body2">No meaning available</Typography>
                            )}
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No meaning available
                        </Typography>
                    )}
                </Box>

                <DialogContentText id="alert-dialog-description">
                    Characters will be placed from top-left to bottom-right.
                    This puzzle will be a Hidden message puzzle.
                </DialogContentText>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 500 }}>
                    The hidden message MUST fit the empty cells.
                </Typography>

                {countsMismatch && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            Empty cells ({Number(emptyCellCount).toLocaleString()}) and selected word length ({wordLength}) do not match.
                        </Typography>
                    </Alert>
                )}
            </DialogContent>
            <DialogActions style={{ display: "flex", justifyContent: "space-between", m: 2 }}>
                <CustomButton variant={"contained"} onClick={handleYes} color="primary" disabled={countsMismatch}>
                    Yes
                </CustomButton>
                <CustomButton variant={"contained"} onClick={handleNo} color="secondary" autoFocus>
                    No
                </CustomButton>
            </DialogActions>
        </Dialog>
    );
}

